import React, { useEffect, useState } from 'react'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import { CLOUD_FUNCTIONS_URL } from '../firebase'
import { stripeConnectService } from '../services/StripeConnectService'

const planOptions = {
  basic: {
    label: 'Starter Plan',
    description: 'Best for new founders and small teams',
    amount: 4700
  },
  pro: {
    label: 'Growth Plan',
    description: 'Advanced automation and campaign growth tools',
    amount: 9700
  },
  enterprise: {
    label: 'Scale Plan',
    description: 'High-volume support and premium onboarding',
    amount: 19700
  }
}

function formatCurrency(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(cents / 100)
}

function normalizePriceValue(price) {
  const amount = Number(price)
  if (Number.isNaN(amount)) return 0
  return amount > 1000 ? amount : amount * 100
}

const PartnerSignup = () => {
  const { partnerCode } = useParams()
  const [searchParams] = useSearchParams()
  const [partner, setPartner] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [selectedPlan, setSelectedPlan] = useState('basic')
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [paymentComplete, setPaymentComplete] = useState(false)
  const [paymentCancelled, setPaymentCancelled] = useState(false)

  const partnerBranding = partner?.brandingConfig || {}
  const brandLogo = partnerBranding.logoUrl || partner?.logoUrl
  const brandColor = partnerBranding.primaryColor || '#10b981'
  const brandFontColor = partnerBranding.secondaryColor || '#111827'
  const brandBackgroundColor = partnerBranding.backgroundColor || '#f8fafc'
  const brandCardColor = partnerBranding.cardColor || '#ffffff'
  const brandDarkColor = partnerBranding.primaryColor ? partnerBranding.primaryColor : '#059669'
  const brandHeadline = partnerBranding.heroHeadline || `Buy through ${partner?.companyName || 'your partner'}`
  const brandSubheadline = partnerBranding.heroSubheadline || 'Complete your purchase with partner-specific pricing and support. Once payment is successful, continue with your account signup.'
  const partnerCompanyName = partner?.companyName || 'Your Partner'
  const partnerPricing = partner?.customPricing
  const effectivePlanOptions = {
    basic: {
      label: partnerPricing?.basicPlan?.name || planOptions.basic.label,
      description: planOptions.basic.description,
      amount: partnerPricing?.basicPlan?.price != null ? normalizePriceValue(partnerPricing.basicPlan.price) : planOptions.basic.amount,
      features: partnerPricing?.basicPlan?.features || []
    },
    pro: {
      label: partnerPricing?.proPlan?.name || planOptions.pro.label,
      description: planOptions.pro.description,
      amount: partnerPricing?.proPlan?.price != null ? normalizePriceValue(partnerPricing.proPlan.price) : planOptions.pro.amount,
      features: partnerPricing?.proPlan?.features || []
    },
    enterprise: {
      label: partnerPricing?.enterprisePlan?.name || planOptions.enterprise.label,
      description: planOptions.enterprise.description,
      amount: partnerPricing?.enterprisePlan?.price != null ? normalizePriceValue(partnerPricing.enterprisePlan.price) : planOptions.enterprise.amount,
      features: partnerPricing?.enterprisePlan?.features || []
    }
  }

  const heroAccentStyle = {
    background: `${brandColor}20`,
    borderColor: `${brandColor}40`
  }

  const primaryButtonStyle = {
    backgroundColor: brandColor,
    borderColor: brandColor
  }

  const glassCardStyle = {
    backgroundColor: 'rgba(255, 255, 255, 0.72)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    borderColor: brandColor
  }

  const brandBorderStyle = {
    borderColor: brandColor
  }

  useEffect(() => {
    const planFromParams = searchParams.get('plan')
    const discountFromParams = parseInt(searchParams.get('discount'), 10)

    if (planFromParams && planOptions[planFromParams]) {
      setSelectedPlan(planFromParams)
    }

    if (searchParams.get('payment_success') === 'true') {
      setPaymentComplete(true)
    }

    if (searchParams.get('payment_cancelled') === 'true') {
      setPaymentCancelled(true)
    }
  }, [searchParams])

  useEffect(() => {
    const fetchPartner = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const response = await fetch(`${CLOUD_FUNCTIONS_URL}/getPartnerByCode?partnerCode=${encodeURIComponent(partnerCode)}`)

        if (!response.ok) {
          const payload = await response.json().catch(() => null)
          console.error('Partner lookup failed:', response.status, payload)
          setError('Partner not found. Please verify the link or contact the partner directly.')
          setPartner(null)
          return
        }

        const data = await response.json()

        if (!data.success || !data.partner) {
          setError(data.error || 'Unable to load partner details. Please refresh or try again later.')
          setPartner(null)
          return
        }

        setPartner({ id: data.partner.id, ...data.partner.data })
      } catch (fetchError) {
        console.error('Error fetching partner data:', fetchError)
        setError('Unable to load partner details. Please refresh or try again later.')
      } finally {
        setIsLoading(false)
      }
    }

    if (partnerCode) {
      fetchPartner()
    } else {
      setError('Invalid partner link.')
      setIsLoading(false)
    }
  }, [partnerCode])

  const currentPlan = effectivePlanOptions[selectedPlan]
  const rawDiscount = parseInt(searchParams.get('discount') || '0', 10)
  const discountValue = Number.isNaN(rawDiscount) ? 0 : Math.max(0, rawDiscount)
  const campaign = searchParams.get('campaign') || ''
  const source = searchParams.get('source') || 'partner'
  const discountedAmount = Math.max(0, currentPlan.amount - Math.round((currentPlan.amount * discountValue) / 100))

  const handleCheckout = async () => {
    if (!email) {
      setError('Please enter your email address to continue.')
      return
    }

    if (!partner?.tenantId) {
      setError('This partner is not yet configured for payments. Please contact the partner directly.')
      return
    }

    if (!partner?.stripeAccountId) {
      setError('This partner has not completed Stripe onboarding yet. Please wait for the partner to connect their Stripe account.')
      return
    }

    setCheckoutLoading(true)
    setError(null)

    try {
      const successUrl = `${window.location.origin}/claim-account?success=true&plan=${selectedPlan}&partnerCode=${partnerCode}&campaign=${encodeURIComponent(campaign)}&source=${encodeURIComponent(source)}&discount=${discountValue}&amountPaid=${discountedAmount}&payment_success=true&session_id={CHECKOUT_SESSION_ID}`
      const cancelUrl = `${window.location.origin}/partner/${partnerCode}?payment_cancelled=true`

      const response = await stripeConnectService.createPartnerCheckoutSession({
        partnerTenantId: partner.tenantId,
        customerEmail: email,
        lineItems: [
          {
            price_data: {
              currency: 'usd',
              product_data: {
                name: `${partner.companyName || 'White Label Partner'} - ${currentPlan.label}`
              },
              unit_amount: discountedAmount
            },
            quantity: 1
          }
        ],
        successUrl,
        cancelUrl,
        metadata: {
          partnerCode,
          plan: selectedPlan,
          partnerName: partner.companyName,
          customerName: name
        }
      })

      if (response.url) {
        window.location.href = response.url
      } else {
        setError('Unable to initiate payment. Please try again.')
      }
    } catch (checkoutError) {
      console.error('Partner checkout error:', checkoutError)
      setError(checkoutError.message || 'Unable to create checkout session.')
    } finally {
      setCheckoutLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-genie-teal mx-auto"></div>
          <p className="mt-4 text-gray-700">Loading partner landing page...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-6">
        <div className="max-w-xl rounded-3xl border border-gray-200 bg-white p-10 shadow-lg">
          <h1 className="text-3xl font-semibold text-gray-900">Partner Link Error</h1>
          <p className="mt-4 text-gray-600">{error}</p>
          <p className="mt-6 text-sm text-gray-500">If you arrived here from a partner referral, ask them to resend the link or verify their onboarding.</p>
          <Link to="/" className="mt-6 inline-flex rounded-full bg-genie-teal px-6 py-3 text-sm font-semibold text-white hover:bg-genie-teal-dark">Return to Market Genie</Link>
        </div>
      </div>
    )
  }

  return (
      <div id="partner-page" className="min-h-screen" style={{ backgroundColor: brandBackgroundColor, color: brandFontColor }}>
      <style>{`#partner-page, #partner-page :not(button):not(button *):not(input):not(input *):not(textarea):not(textarea *):not(select):not(select *) { color: ${brandFontColor} !important; }`}</style>
      <div className="relative overflow-hidden" style={{ backgroundColor: brandBackgroundColor }}>
        <div className="relative mx-auto max-w-8xl px-6 py-16 sm:px-8 lg:px-12">
          <div className="space-y-12">
            <div className="space-y-8">
              <div className="flex justify-center">
                <div className="flex items-center gap-4">
                  <div className="flex h-28 w-28 items-center justify-center rounded-full p-3" style={{ backgroundColor: brandBackgroundColor }}>
                    {brandLogo ? (
                      <img
                        src={brandLogo}
                        alt={`${partnerCompanyName} logo`}
                        className="h-24 w-24 object-contain"
                      />
                    ) : (
                      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gray-100 text-2xl font-bold" style={{ color: brandFontColor }}>
                        {partnerCompanyName.charAt(0)}
                      </div>
                    )}
                  </div>

                  <div
                    className="inline-flex items-center gap-4 rounded-full px-5 py-3 text-base font-semibold shadow-sm"
                    style={{ backgroundColor: `${brandColor}20`, color: brandDarkColor }}
                  >
                    <span className="text-lg font-semibold text-gray-900">{partnerCompanyName} is partnering with Market Genie</span>
                  </div>
                </div>
              </div>

              <div className="mx-auto max-w-5xl space-y-6 text-center">
                <div className="space-y-4">
                  <h1 className="text-6xl font-bold tracking-tight text-gray-900 sm:text-7xl">{brandHeadline}</h1>
                  <p className="text-2xl leading-9 text-gray-600">{brandSubheadline}</p>
                </div>
              </div>

              <div className="space-y-6 text-center mx-auto max-w-5xl">
                <div className="rounded-4xl border p-8 shadow-sm mx-auto" style={{ ...brandBorderStyle, backgroundColor: brandCardColor }}>
                  <h2 className="text-3xl font-semibold text-gray-900">What Market Genie does</h2>
                  <p className="mt-4 text-gray-600">Market Genie uses AI automation to manage lead capture, nurture campaigns, and follow-up sequences so you can turn more prospects into customers without manual effort.</p>
                </div>

                <div className="grid gap-6 lg:grid-cols-2">
                  <div className="rounded-4xl border p-10 shadow-sm min-h-[30rem] flex flex-col justify-between" style={{ ...brandBorderStyle, backgroundColor: brandCardColor }}>
                    <div>
                      <p className="text-sm font-semibold uppercase tracking-[0.3em] text-gray-500">What this does</p>
                      <p className="mt-4 text-base text-gray-700">Market Genie automates your outreach, builds email funnels, and turns cold prospects into customers with AI-powered workflows.</p>
                    </div>
                    <div className="mt-8 space-y-5 text-left">
                      <div>
                        <p className="font-semibold text-gray-900">AI email campaigns</p>
                        <p className="mt-2 text-sm text-gray-600">Generate high-converting outreach that warms up leads automatically.</p>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Lead capture funnels</p>
                        <p className="mt-2 text-sm text-gray-600">Build conversion funnels with landing pages, forms, and follow-up sequences.</p>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Performance insights</p>
                        <p className="mt-2 text-sm text-gray-600">Track opens, clicks, and campaign performance from one dashboard.</p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-4xl border p-10 shadow-sm min-h-[30rem] flex flex-col justify-between" style={{ ...brandBorderStyle, backgroundColor: brandCardColor }}>
                    <div>
                      <p className="text-sm font-semibold uppercase tracking-[0.3em] text-gray-500">Why this matters</p>
                      <p className="mt-4 text-base text-gray-700">It gives you convert-ready campaigns, follow-up sequences, and lead capture tools in one platform — all backed by partner support.</p>
                    </div>
                    <div className="mt-8 space-y-6 text-left">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Built for founders and agencies</h3>
                        <ul className="mt-3 space-y-2 text-gray-700">
                          <li>• AI-powered email and campaign automation</li>
                          <li>• Lead capture, nurture, and conversion tools</li>
                          <li>• Pre-built workflows for faster results</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">What you get</h3>
                        <ul className="mt-3 space-y-2 text-gray-700">
                          <li>• Personalized outreach at scale</li>
                          <li>• Real-time campaign analytics</li>
                          <li>• Partner-exclusive onboarding support</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-8xl px-6 sm:px-8 lg:px-12 pb-12">
          <main className="space-y-10 mx-auto max-w-5xl">
            <section className="rounded-4xl border p-10 shadow-sm" style={{ ...brandBorderStyle, backgroundColor: brandCardColor }}>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-3xl font-semibold text-gray-900">Why buy through {partnerCompanyName}?</h2>
                  <p className="mt-4 text-gray-600">Get partner-specific pricing, support, and direct alignment with the team that referred you.</p>
                </div>
              </div>

              <div className="mt-10 grid gap-6 md:grid-cols-2">
                <div className="rounded-3xl border p-6 shadow-sm" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', borderColor: brandColor }}>
                  <p className="font-semibold text-gray-900">Built for your growth</p>
                  <p className="mt-3 text-sm text-gray-600">Everything is designed to help you launch campaigns faster and convert more leads.</p>
                </div>
                <div className="rounded-3xl border p-6 shadow-sm" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', borderColor: brandColor }}>
                  <p className="font-semibold text-gray-900">Partner-driven value</p>
                  <p className="mt-3 text-sm text-gray-600">This offer comes through a trusted partner, making onboarding smoother and more reliable.</p>
                </div>
              </div>
            </section>

            <section className="rounded-4xl border pt-10 pb-6 px-10 shadow-sm" style={{ ...brandBorderStyle, backgroundColor: brandCardColor }}>
              <h2 className="text-3xl font-semibold text-gray-900">Ready to get started?</h2>
              <p className="mt-4 text-gray-600">Purchase below, then complete your account signup to access the Market Genie dashboard.</p>
              <div className="mt-8 rounded-4xl border p-8" style={{ ...brandBorderStyle, backgroundColor: `${brandColor}18` }}>
                <div className="rounded-3xl border p-6 shadow-sm" style={{ ...brandBorderStyle, backgroundColor: brandCardColor }}>
                  <p className="text-sm font-semibold uppercase tracking-[0.3em]" style={{ color: brandFontColor }}>Choose a plan</p>
                  <div className="mt-4 grid gap-4 md:grid-cols-3">
                    {Object.entries(effectivePlanOptions).map(([key, plan]) => (
                      <button
                        type="button"
                        key={key}
                        onClick={() => setSelectedPlan(key)}
                        className="w-full text-left rounded-3xl border p-4 transition shadow-sm hover:border-gray-400"
                        style={{
                          ...glassCardStyle,
                          borderColor: selectedPlan === key ? brandDarkColor : brandColor,
                          backgroundColor: selectedPlan === key ? 'rgba(255, 255, 255, 0.88)' : glassCardStyle.backgroundColor
                        }}
                      >
                        <div className="flex items-center justify-between gap-4" style={{ color: brandFontColor }}>
                          <div>
                            <p className="text-base font-semibold" style={{ color: brandFontColor }}>{plan.label}</p>
                            <p className="text-sm" style={{ color: brandFontColor }}>{plan.description}</p>
                          </div>
                          <div className="text-xl font-bold" style={{ color: brandFontColor }}>{formatCurrency(plan.amount)}</div>
                        </div>
                        {plan.features?.length > 0 && (
                          <ul className="mt-4 space-y-1 text-sm" style={{ color: brandFontColor }}>
                            {plan.features.slice(0, 3).map((feature, index) => (
                              <li key={index}>• {feature}</li>
                            ))}
                          </ul>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <div id="partner-checkout-form" className="mt-8 rounded-4xl border p-10 shadow-sm" style={{ ...brandBorderStyle, backgroundColor: brandCardColor }}>
              <h2 className="text-3xl font-semibold text-gray-900">Purchase now</h2>
              <p className="mt-3 text-gray-600">Complete checkout to pay for Market Genie through {partnerCompanyName}. After payment, you will finish sign up and access the platform.</p>

              <div className="mt-8 grid gap-6 lg:grid-cols-[1.4fr_0.8fr]">
                <div className="space-y-6">
                  <div className="rounded-3xl border p-6 shadow-sm" style={glassCardStyle}>
                    <p className="text-sm font-semibold uppercase tracking-[0.3em] text-gray-500">Your selected plan</p>
                    <p className="mt-3 text-xl font-semibold text-gray-900">{currentPlan.label}</p>
                    <p className="mt-2 text-sm text-gray-600">{currentPlan.description}</p>
                  </div>

                  <div className="rounded-3xl border p-6 shadow-sm" style={glassCardStyle}>
                    <p className="text-sm font-semibold uppercase tracking-[0.3em] text-gray-500">Your investment</p>
                    <p className="mt-3 text-4xl font-bold text-gray-900">{formatCurrency(discountedAmount)}</p>
                    <p className="mt-2 text-sm text-gray-600">Secure Stripe payment for {partnerCompanyName}.</p>
                  </div>
                </div>

                <div className="rounded-4xl border p-6 shadow-sm" style={{ ...glassCardStyle, borderColor: brandColor }}>
                  <h3 className="text-xl font-semibold text-gray-900">Ready to be your own boss</h3>
                  <p className="mt-3 text-sm text-gray-600">Enter your name and email, then proceed to Stripe.</p>

                  <div className="mt-6 space-y-4">
                    <label className="block">
                      <span className="text-sm font-medium text-gray-700">Full name</span>
                      <input
                        type="text"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        placeholder="Your name"
                        className="mt-2 w-full rounded-2xl border px-4 py-3 text-sm text-gray-900 outline-none focus:border-genie-teal focus:ring-2 focus:ring-genie-teal/10"
                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.72)', borderColor: brandColor }}
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm font-medium text-gray-700">Email address</span>
                      <input
                        type="email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        placeholder="you@example.com"
                        className="mt-2 w-full rounded-2xl border px-4 py-3 text-sm text-gray-900 outline-none focus:border-genie-teal focus:ring-2 focus:ring-genie-teal/10"
                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.72)', borderColor: brandColor }}
                      />
                    </label>
                    {partner?.stripeAccountId ? (
                      <button
                        type="button"
                        onClick={handleCheckout}
                        disabled={checkoutLoading}
                        className="inline-flex w-full items-center justify-center rounded-2xl px-6 py-3 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-70"
                        style={{ backgroundColor: brandDarkColor }}
                      >
                        {checkoutLoading ? 'Starting checkout…' : `Pay ${formatCurrency(discountedAmount)}`}
                      </button>
                    ) : (
                      <div className="rounded-2xl border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-900">
                        Stripe onboarding is pending. Checkout is unavailable until the partner connects Stripe.
                      </div>
                    )}
                    {error && <p className="text-sm text-red-600">{error}</p>}
                  </div>
                </div>
              </div>
            </div>

            {(partnerBranding.footerSocial || partnerBranding.footerPhone || partnerBranding.footerAddress || partnerBranding.footerHours) && (
              <div className="mx-auto max-w-5xl rounded-4xl border p-8 shadow-sm" style={{ ...brandBorderStyle, backgroundColor: brandCardColor }}>
                <h3 className="text-lg font-semibold text-gray-900 text-center">More ways to connect</h3>
                <div className="mt-6 grid gap-6 md:grid-cols-2">
                  {partnerBranding.footerSocial && (
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Social</p>
                      <p className="mt-2 text-sm text-gray-600">{partnerBranding.footerSocial}</p>
                    </div>
                  )}
                  {partnerBranding.footerPhone && (
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Phone</p>
                      <p className="mt-2 text-sm text-gray-600">{partnerBranding.footerPhone}</p>
                    </div>
                  )}
                  {partnerBranding.footerAddress && (
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Address</p>
                      <p className="mt-2 text-sm text-gray-600">{partnerBranding.footerAddress}</p>
                    </div>
                  )}
                  {partnerBranding.footerHours && (
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Office hours</p>
                      <p className="mt-2 text-sm text-gray-600">{partnerBranding.footerHours}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
  )
}

export default PartnerSignup
